import axios from "axios";
import { DATA_CENTER_URL_API_NAME, EXTENSION_IDENTIFIER, IS_SANDBOX, API_KEY_SET } from "../config/serviceConstant";
import { DATA_CENTER_MAP } from "../config/zohoConstant";
import { handleApiError } from "./utils/handleApiError";
import { env } from "util/env";

const ZOHO = window.ZOHO;

const EDIT_VIEW = "EditView";

const getAuthUrl = async ({ zuid, orgId, apiKey, zapiKey, dataCenterUrl }) => {
  const resp = await axios.request({
    url: env.REACT_APP_ADMIN_SERVER_URL + "/auth/zoho/authenticate",
    method: "POST",
    data: {
      zuid: zuid,
      orgId: orgId,
      apiKey: apiKey,
      zapiKey: zapiKey,
      crmApiDomain: dataCenterUrl,
      accountsUrl: "https://accounts.zoho." + dataCenterUrl?.split("https://www.zohoapis.")?.[1],
      connName: env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
      scope: [
        "ZohoCRM.users.CREATE",
        "ZohoCRM.users.READ",
        // "ZohoCRM.users.UPDATE",
        "ZohoCRM.org.READ",
        "ZohoCRM.org.UPDATE",
        "ZohoCRM.settings.CREATE",
        "ZohoCRM.settings.READ",
        "ZohoCRM.settings.UPDATE",
        "ZohoCRM.coql.READ",
        "ZohoCRM.modules.CREATE",
        "ZohoCRM.modules.READ",
        "ZohoCRM.modules.UPDATE",
        "ZohoCRM.send_mail.all.CREATE",
        "ZohoCRM.modules.ALL",
        "ZohoCRM.settings.emails.READ",
        "ZohoCRM.settings.mailmerge.CREATE",
        "ZohoWriter.documentEditor.ALL",
        // "ZohoWriter.merge.ALL",
        "ZohoWriter.merge.READ",
        "ZohoWriter.merge.CREATE",
        "ZohoWriter.merge.UPDATE",
      ],
      redirectUrl: `${env.REACT_APP_ADMIN_SERVER_URL}/auth/zoho/callback`,
    },
  });

  return { data: resp?.data?.authUrl };
};

const openPopup = async ({ authUrl }) => {
  const popup = window.open(authUrl, "authUrlWindow", "height=640,width=1024");

  if (!popup || popup.closed || typeof popup.closed == "undefined") {
    alert("Popup window was blocked by the browser. Please allow popups for this site.");
    return null;
  }

  return popup;
};

const createExtensionWidgets = async ({ dataCenterUrl, apiKey, orgId }) => {
  try {
    const connname = env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho";
    const url = `${env.REACT_APP_ADMIN_SERVER_URL}/data/zoho/crm/extwidgets`;
    const headers = {
      connname,
      orgid: orgId,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
    };
    const data = {
      widgetName: "Email by Easy Pluginz",
      widgetUrl: `https://widgets.v1.easyemailforzohocrm.easy-pluginz.com/buttons`,
      widgetDescription: "This widget you can be used in any Standard and Custom Module to send Emails",
      widgetType: "CUSTOM_BUTTON",
      apiDomain: dataCenterUrl,
    };
    const config = {
      url,
      data,
      headers,
      method: "POST",
    };
    const resp = await axios.request(config);
  } catch (error) {
    return {
      data: null,
      error: handleApiError("createExtensionWidgets", error),
    };
  }
};

const handleAuthCheckZoho = async ({ headers, dataCenterUrl }) => {
  try {
    if (
      !dataCenterUrl ||
      !headers.apikey ||
      !headers.orgid ||
      !headers.accountsurl ||
      !headers.connname ||
      !headers.dataCenterurlvariablename
    ) {
      return false;
    }
    // for zoho auth verify
    const authCheckConfig = {
      url: `${env.REACT_APP_ADMIN_SERVER_URL}/auth/zoho/verifyauth`,
      headers: headers,
    };
    const authCheck = await axios(authCheckConfig);

    return authCheck?.data?.org?.length > 0 ? true : false;
  } catch (handleAuthCheckZohoError) {
    return false;
  }
};

const saveDataCenterUrl = async ({ ZOHO, IS_SANDBOX, EXTENSION_IDENTIFIER, DATA_CENTER_URL_API_NAME }) => {
  try {
    let dataCenterUrl = null;
    if (IS_SANDBOX === "true") {
      dataCenterUrl = `https://plugin-${EXTENSION_IDENTIFIER}.zohosandbox.com`;
    } else {
      const currentEnv = await ZOHO.CRM.CONFIG.GetCurrentEnvironment();
      dataCenterUrl = DATA_CENTER_MAP?.[currentEnv?.deployment] || "";
    }

    const dataCenterData = {
      apiname: DATA_CENTER_URL_API_NAME,
      value: dataCenterUrl,
    };

    await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", dataCenterData);

    return { data: dataCenterUrl, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("saveDataCenterUrl", error),
    };
  }
};

const getAccessToken = async ({ apikey, orgid }) => {
  try {
    const url = `${env.REACT_APP_ADMIN_SERVER_URL}/auth/zoho/accesstoken`;
    // const url = "https://api.easy-pluginz.com/admin/v2/auth/zoho/accesstoken";
    const access_config = {
      url: url,
      headers: {
        apikey,
        orgid,
        connname: env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
      },
    };
    const resp = await axios(access_config);
    return { data: resp?.data?.data?.accessToken, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("getAccessToken", error),
    };
  }
};

const transformOrgVariablesData = async (orgVariables) => {
  let apikey = orgVariables?.Success?.Content?.[`${env.REACT_APP_EXTENSION_IDENTIFIER}__API_KEY`]?.value;
  let zapiKey = orgVariables?.Success?.Content?.[`${env.REACT_APP_EXTENSION_IDENTIFIER}__ZAPI_Key`]?.value;
  let orgid = orgVariables?.Success?.Content?.[`${env.REACT_APP_EXTENSION_IDENTIFIER}__Organization_ID`]?.value;
  let dataCenterUrl = orgVariables?.Success?.Content?.[`${env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`]?.value;

  return {
    apikey,
    zapiKey,
    orgid,
    dataCenterUrl,
  };
};

const getOrgData = async ({ ZOHO, API_KEY_SET, IS_SANDBOX, EXTENSION_IDENTIFIER, DATA_CENTER_URL_API_NAME }) => {
  const orgVariables = await ZOHO.CRM.API.getOrgVariable({ apiKeys: API_KEY_SET });
  const { apikey, orgid, zapiKey, dataCenterUrl: savedDataCenterUrl } = await transformOrgVariablesData(orgVariables);

  const { data: accessToken } = await getAccessToken({ apikey, orgid });

  const hasDCUrl = savedDataCenterUrl && savedDataCenterUrl !== "null";
  let dataCenterUrl = null;

  if (hasDCUrl) {
    dataCenterUrl = savedDataCenterUrl;
  } else {
    const { data: newDataCenterUrl } = await saveDataCenterUrl({ ZOHO, IS_SANDBOX, EXTENSION_IDENTIFIER, DATA_CENTER_URL_API_NAME });
    dataCenterUrl = newDataCenterUrl;
  }

  const orgInfoResp = await ZOHO.CRM.CONFIG.getOrgInfo();
  const { primary_zuid, primary_email } = await orgInfoResp?.org?.[0];

  const data = {
    apiKey: apikey,
    zapiKey,
    orgId: orgid,
    zuid: primary_zuid,
    adminEmail: primary_email,
    dataCenterUrl,
    accessToken: accessToken ? accessToken : "",
    zohoAuthenticated: accessToken ? true : false,
  };

  return data;
};

export const initializeZohoAuth = async ({ height, width }) => {
  try {
    const settledPromise = await new Promise(async (resolve, reject) => {
      let initialData = null;
      let initZoho = null;
      // ------------
      let orgData = null;
      // ------------

      ZOHO.embeddedApp.on("PageLoad", async function (data) {
        // ------------
        orgData = await getOrgData({ ZOHO, API_KEY_SET, IS_SANDBOX, EXTENSION_IDENTIFIER, DATA_CENTER_URL_API_NAME });
        // ------------
        const relatedList = Boolean(data?.related_list);
        const editViewButtonPosition = data?.ButtonPosition === EDIT_VIEW;
        const convertToArray = editViewButtonPosition || relatedList;
        const newEntityId = convertToArray ? [data?.EntityId] : data?.EntityId;
        const tempObj = newEntityId ? { EntityId: newEntityId } : {};
        initialData = { ...data, ...tempObj };

        if (initZoho !== null && orgData !== null) {
          resolve({ initialData, initZoho, ...orgData });
        }
      });

      ZOHO.embeddedApp
        .init()
        .then(() => {
          initZoho = true;

          if (height && width) {
            ZOHO.CRM.UI.Resize({ height, width });
          }

          if (initialData !== null && orgData !== null) {
            resolve({ initialData, initZoho, ...orgData });
          }
        })
        .catch((err) => {
          const customError = new Error("Failed to initialized ZOHO");
          reject(customError);
        });
    });
    return { data: settledPromise, error: null };
  } catch (error) {
    // return {
    //   data: null,
    //   error: handleApiError("initializeZohoAuth", error),
    // };
    throw handleApiError("initializeZohoAuth", error);
  }
};

export const handleAuthenticateZoho = async ({ zuid, orgId, apiKey, zapiKey, dataCenterUrl }) => {
  try {
    if (!zuid || !orgId || !apiKey || !zapiKey || !dataCenterUrl) {
      throw Error("critical parameters not found");
    }

    const { data: authUrl } = await getAuthUrl({
      zuid,
      orgId,
      apiKey,
      zapiKey,
      dataCenterUrl,
    });

    if (!authUrl) {
      throw new Error("authUrl not found");
    }

    const popup = await openPopup({ authUrl });

    if (!popup) {
      throw new Error("popup not working");
    }

    const headers = {
      orgid: orgId,
      apikey: apiKey,
      accountsurl: dataCenterUrl,
      connname: env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
      dataCenterurlvariablename: env.REACT_APP_EXTENSION_IDENTIFIER + "__Data_Center_URL",
    };

    const settledPromise = await new Promise((resolve, reject) => {
      const authCheckIntervalId = setInterval(async () => {
        const zohoAuthenticated = await handleAuthCheckZoho({
          headers,
          dataCenterUrl,
        });

        if (zohoAuthenticated) {
          const { data: accessToken } = await getAccessToken({ apiKey, orgId });
          clearInterval(authCheckIntervalId);
          resolve({ zohoAuthenticated, accessToken });
        }
      }, 5000);
    });

    if (!settledPromise?.zohoAuthenticated) {
      throw new Error("zoho authentication problem");
    }

    popup.close(); // for some security policy, popup.close() does not work.
    await createExtensionWidgets({ dataCenterUrl, apiKey, orgId });

    return { data: settledPromise, error: null };
  } catch (error) {
    return {
      data: null,
      error: handleApiError("handleAuthenticateZoho", error),
    };
  }
};

export const handleRevokeZoho = async ({ orgId, apiKey }) => {
  try {
    const authUrlResp = await axios.request({
      url: env.REACT_APP_ADMIN_SERVER_URL + "/auth/zoho/revoke",
      method: "GET",
      headers: {
        orgid: orgId,
        apikey: apiKey,
        connname: env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
      },
    });

    if (authUrlResp?.data?.error === null) {
      return {
        data: { zohoAuthenticated: false, accessToken: "" },
        error: null,
      };
    }
  } catch (error) {
    return {
      data: null,
      error: handleApiError("handleRevokeZoho", error),
    };
  }
};

export const auth = {
  getAccessToken,
  initializeZohoAuth,
  handleRevokeZoho,
  handleAuthenticateZoho,
};
